<template>
	<div class="field-detail">
		<BlockContainer v-if="showFieldWarning">
			<BlockNotifications
				:message="'Field position overlaps with another.'"
				:blockState="'warning'"
			/>
		</BlockContainer>
		<div class="field-detail__common-fields">
			<Input
				ref="row"
				ariaLabel="Row"
				className="field__input"
				label="Row"
				name="row"
				:required="true"
				:inputType="'Number'"
				:minValue="minRowColValue"
				v-model="source.row"
				:isNegativeAllow="false"
				@enterKeyUp="changeFocus('row')"
				:min="0"
				:max="400"
			/>
			<Input
				ref="dot"
				ariaLabel="Dot"
				className="field__input"
				label="Dot"
				name="dot"
				:required="true"
				:inputType="'Number'"
				:maxValue="maxDotValue"
				:minValue="minDotValue"
				v-model="source.dot"
				:isNegativeAllow="false"
				@enterKeyUp="changeFocus('dot')"
				:min="0"
				:max="11"
			/>
			<Input
				ref="column"
				ariaLabel="Column"
				className="field__input"
				label="Column"
				name="column"
				:required="true"
				:inputType="'Number'"
				:minValue="minRowColValue"
				v-model="source.col"
				:isNegativeAllow="false"
				@enterKeyUp="changeFocus('column')"
				:min="0"
				:max="200"
			/>
		</div>
		<div class="field-detail__dropdown-fields">
			<div class="field-detail__type-selector">
				<Dropdown
					label="Field Type"
					:autosuggestion="false"
					:displayHelp="false"
					:options="optionFieldsType"
					v-model="impactType"
					@change="changeFocusFrom"
				/>
			</div>
			<div class="field-detail__dropdown-menu">
				<MenuButton
					v-if="
						selectedCodeByFieldType &&
							showAddCodeDetails &&
							hasNewCodePermission
					"
					:items="codeOptionItems"
					v-model="codeCreateValue"
					@change="addCode"
				/>
			</div>
		</div>

		<!-- Only shows for Field Type - Static -->
		<div
			class="field-detail__static-fields"
			v-show="selectedStaticByFieldType"
		>
			<Input
				ref="staticValue"
				ariaLabel="Aria Label"
				className="field__input"
				label="Static Value"
				name="StaticValue"
				:required="true"
				v-model="source.staticValue"
			/>
			<Input
				ref="description"
				ariaLabel="Aria Label"
				className="field__input"
				label="Description"
				name="description"
				:required="true"
				v-model="source.description"
			/>
		</div>

		<!-- Only shows for Field Type - Customer Input -->
		<div
			class="field-detail__customer-input-fields"
			v-show="selectedCustomerInputByFieldType"
		>
			<Autosuggestion
				ref="prompt"
				ariaLabel="Aria Label"
				className="field__input"
				label="Prompt"
				name="prompt"
				:required="true"
				:suggestionsList="prompTypes"
				v-model="source.keyboardPrompt"
			/>
		</div>

		<!-- Only shows for Field Type - Code -->
		<div class="field-detail__code-fields" v-show="selectedCodeByFieldType">
			<SearchFinder
				ref="searchFinder"
				:label="'Select Code'"
				:placeholder="'Search code ID or description'"
				:apiOptions="{
					params: {
						count: 10,
						page: 1,
						sortBy: 'id',
						sortDirection: 'asc'
					}
				}"
				v-model="snippetSelected"
				@selected="getSnippetSelected"
				@click="
					val => {
						this.displayModalCodeLookUp = val;
					}
				"
			/>
			<CodeLookUpModal
				v-model="displayModalCodeLookUp"
				@selected="getSnippetSelected"
			/>
			<div class="field-detail__truncate">
				<Fieldset
					:legend="'Truncation'"
					:variant="'fieldset--no-borders'"
				>
					<template #fieldset-content>
						<RadioButtonGroup
							:items="truncationOptions"
							v-model="source.truncate"
						/>
					</template>
				</Fieldset>
				<Input
					ref="characterNumber"
					v-show="hasCharacterNumber"
					ariaLabel="Aria Label"
					className="field__input"
					label="Character #"
					name="characterNumber"
					:maxValue="maxCharacterNumber"
					:minValue="minCharacterNumber"
					:required="true"
					:inputType="'Number'"
					:isNegativeAllow="false"
					v-model="characterNumber"
				/>
			</div>
			<div class="form-builder__detail-btn">
				<Button
					v-if="showAddCodeDetails && showCodeDetails"
					className="btn--small"
					id="defaultButton"
					labelText="View Code Detail"
					:btnClick="viewCodeDetails"
				/>
			</div>
			<CodeListModal
				:search="parameters.search"
				:pageProp="page"
				v-model="showCodeListModal"
			/>
			<CodeNewCopyModal
				v-model="showNewCodeModal"
				@show="closeCodeSelection"
			/>
		</div>
	</div>
</template>

<script>
import Autosuggestion from '@/components/forms/autosuggestion/autosuggestion';
import Input from '@/components/forms/input/input';
import Dropdown from '@/components/forms/dropdown/dropdown.vue';
import SearchFinder from '@/components/forms/search/search-finder';
import Fieldset from '@/components/forms/fieldset/fieldset';
import RadioButtonGroup from '@/components/forms/selection-control/radiobutton/radiobutton-group';
import MenuButton from '@/components/forms/button/menu-button';
import Button from '@/components/forms/button/button';
import CodeLookUpModal from '@/views/form-designer/code-lookup-modal';
import BlockContainer from '@/components/notifications/block-notifications/block-notifications-container';
import BlockNotifications from '@/components/notifications/block-notifications/block-notifications';
import { isEmpty, compare as objCompare } from '@/utils/object';
import CodeListModal from '@/views/code-manager/code-list-modal.vue';
import CodeNewCopyModal from '@/views/code-manager/new-copy-of-code-settings-modal.vue';
import PaginationPage from '@/mixins/infiniteScroll/page';
import { mapGetters } from 'vuex';
import buildImpactField from './impact-field';
import _ from 'lodash';
import Vue from 'vue';

export default {
	name: 'FieldDetail',
	mixins: [PaginationPage],
	components: {
		Autosuggestion,
		Input,
		Dropdown,
		SearchFinder,
		Fieldset,
		RadioButtonGroup,
		MenuButton,
		Button,
		CodeLookUpModal,
		BlockContainer,
		BlockNotifications,
		CodeListModal,
		CodeNewCopyModal
	},
	model: {
		prop: 'source',
		event: 'change'
	},
	props: {
		source: {
			type: Object,
			default: () => {}
		},
		impactList: {
			type: Array,
			default: () => {}
		},
		isEditObject: {
			type: Object,
			default: () => {}
		},
		snippet: {
			type: Object,
			default: () => {}
		},
		showAddCodeDetails: {
			type: Boolean,
			default: true
		},
		resetValue: {
			type: Boolean,
			default: false
		},
		rangeValues: {
			type: Object
		},
		setRowFocus: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			codeCreateValue: {
				text: 'Add Code',
				value: 'default'
			},
			codeOptionItems: [
				{
					text: 'New Code',
					value: 'N'
				},
				{
					text: 'Copy Of',
					value: 'C'
				}
			],
			optionFieldsType: [
				{ text: 'Static', value: 'S' },
				{ text: 'Client Input', value: 'I' },
				{ text: 'Code', value: 'C' }
			],
			truncationOptions: [
				{
					labelText: 'None',
					disabled: false,
					value: null
				},
				{
					labelText: 'Left',
					disabled: false,
					value: 'L'
				},
				{
					labelText: 'Right',
					disabled: false,
					value: 'R'
				}
			],
			prompTypes: [],
			displayModalCodeLookUp: false,
			snippetSelected: {},
			showNewCodeModal: false,
			showCodeListModal: false,
			parameters: {}
		};
	},
	computed: {
		...mapGetters('codemanager', ['resetSnippet', 'codeSelected']),

		selectedStaticByFieldType() {
			return this.source.impactType === 'S';
		},
		selectedCustomerInputByFieldType() {
			return this.source.impactType === 'I';
		},
		selectedCodeByFieldType() {
			return this.source.impactType === 'C';
		},

		hasCharacterNumber() {
			const { truncate } = this.source || {};
			if (truncate) {
				return ['L', 'R'].includes(truncate);
			}
			return false;
		},
		showFieldWarning() {
			const { row, dot, col } = this.source || {};

			if (
				row !== undefined &&
				row !== '' &&
				dot !== undefined &&
				dot !== '' &&
				col !== undefined &&
				col !== ''
			) {
				if (!isEmpty(this.isEditObject)) {
					let fieldObj = Object.assign(
						{},
						{ row: row, dot: dot, col: col }
					);
					if (objCompare(fieldObj, this.isEditObject)) {
						return false;
					}
				}

				let impactsList = [...this.impactList];
				return impactsList.some(
					item =>
						item.row === Number(row) &&
						item.dot === Number(dot) &&
						item.col === Number(col)
				);
			}
			return false;
		},
		showCodeDetails() {
			return !isEmpty(this.snippetSelected);
		},
		hasNewCodePermission() {
			return this.validatePermission('forms.create');
		},
		impactType: {
			get() {
				return this.source.impactType;
			},
			set(value) {
				this.handlingFileType(value);
			}
		},
		characterNumber: {
			get() {
				return this.source.characterNumber;
			},
			set(value) {
				if (!value) {
					this.source.characterNumber = 1;
				} else {
					this.source.characterNumber = value;
				}
			}
		},

		maxCharacterNumber() {
			return _.get(this.rangeValues, 'characterNumber.maxValue', 50);
		},
		minCharacterNumber() {
			return _.get(this.rangeValues, 'characterNumber.minValue', 1);
		},
		maxDotValue() {
			return _.get(this.rangeValues, 'dot.maxValue', 0);
		},
		minDotValue() {
			return _.get(this.rangeValues, 'dot.minValue', 0);
		},
		minRowColValue() {
			return _.get(this.rangeValues, 'rowCol.minValue', 0);
		}
	},
	methods: {
		getSnippetSelected(val) {
			this.snippetSelected = val;
			this.snippetSelected.sourceOfSelection = 'FieldDetail';
			this.$store.dispatch(
				'codemanager/codeSelected',
				this.snippetSelected
			);
			this.$emit('selected', this.snippetSelected);
		},
		updateCodeFieldType() {
			const {
				id,
				snippetAttribute,
				snippetFormat,
				snippetCalc,
				snippetJustify,
				canBeUpdated,
				description
			} = this.snippetSelected || {};

			Object.assign(this.source, {
				formsSnippetsId: id,
				description: description,
				attribute: snippetAttribute,
				format: snippetFormat,
				calc: snippetCalc,
				justify: snippetJustify,
				canBeUpdated: canBeUpdated
			});
		},
		getPrompList() {
			let impactsList = [...this.impactList];
			let results = [];
			if (impactsList.length > 0) {
				let impactsListReduced = impactsList.reduce(
					(impacts, impact) => {
						let keyboardPrompt = impact.keyboardPrompt;
						if (keyboardPrompt) {
							impacts.push(keyboardPrompt);
						}
						return impacts;
					},
					[]
				);
				results = [...new Set(impactsListReduced)];
			}
			return results;
		},
		changeFocus(ref) {
			const setFocus = (name, childName) => {
				return this.$refs[name].$refs[childName].focus();
			};
			if (ref === 'row') {
				if (!_.isEmpty(this.source)) {
					if (
						this.source.row !== undefined ||
						this.source.row !== ''
					) {
						setFocus('dot', 'fieldinput');
					}
				}
			} else if (ref === 'dot') {
				if (
					this.source.dot === undefined ||
					this.source.dot === '' ||
					this.source.dot < 0
				) {
					Vue.set(this.source, 'dot', 0);
				}
				setFocus('column', 'fieldinput');
			} else if (ref === 'column') {
				if (
					this.source.col === undefined ||
					this.source.col === '' ||
					this.source.col < 0
				) {
					Vue.set(this.source, 'col', 0);
				}
				this.$emit('focus', true);
			}
		},
		changeFocusFrom(value) {
			if (!value) return;

			const self = this;
			const setFocus = (name, childName) => {
				self.$nextTick(() => {
					return self.$refs[name].$refs[childName].focus();
				});
			};
			if (value === 'S') {
				setFocus('staticValue', 'fieldinput');
			} else if (value === 'I') {
				setFocus('prompt', 'fieldinput');
			} else {
				setFocus('searchFinder', 'searchFinderInput');
			}
		},
		addCode(obj) {
			this.codeCreateValue = {
				text: 'Add Code',
				value: 'default'
			};
			if (obj.value === 'N') {
				this.showCodeListModal = false;
				this.$store.commit('codemanager/SetCodeSelected', {});
				this.showNewCodeModal = true;
				return;
			}

			this.$store.commit('codemanager/SetCodeManager', this.pagingModel);
			this.showNewCodeModal = false;
			this.showCodeListModal = true;
		},
		closeCodeSelection() {
			if (JSON.stringify(this.codeSelected) !== '{}') {
				this.snippetSelected = { ...this.codeSelected };
			}
		},
		viewCodeDetails() {
			this.$store.dispatch(
				'codemanager/codeSelectedDetails',
				this.snippetSelected
			);
			this.$store.dispatch('codemanager/showCodeDetailModal', true);
		},
		handlingFileType(impactType) {
			const obj = this.source;
			let result;
			//Handling object when fieldType is undefined
			if (!impactType) {
				result = buildImpactField({
					col: obj.col,
					dot: obj.dot,
					row: obj.row,
					formsRepositoryId: obj.formsRepositoryId
						? obj.formsRepositoryId
						: 0,

					id: obj.id ? obj.id : 0
				});
			}
			//Handling obj when fieldType is fieldType is static
			else if (impactType === 'S') {
				result = buildImpactField({
					col: obj.col,
					dot: obj.dot,
					row: obj.row,
					impactType: 'S',
					description: obj.impactType === 'S' ? obj.description : '',
					attribute: 'C',
					formsRepositoryId: obj.formsRepositoryId
						? obj.formsRepositoryId
						: 0,
					staticValue: obj.staticValue,
					id: obj.id ? obj.id : 0
				});
			}
			// Handling object when fieldType is customer input (fieldReference === promp)
			else if (impactType === 'I') {
				result = buildImpactField({
					col: obj.col,
					dot: obj.dot,
					row: obj.row,
					impactType: 'I',
					description:
						obj.impactType === 'I' ? obj.keyboardPrompt : '',
					keyboardPrompt: obj.keyboardPrompt,
					formsRepositoryId: obj.formsRepositoryId
						? obj.formsRepositoryId
						: 0,
					id: obj.id ? obj.id : 0
				});
			}
			// handling fieldType when is code
			else if (impactType === 'C') {
				result = buildImpactField({
					col: obj.col,
					dot: obj.dot,
					row: obj.row,
					description: obj.impactType === 'C' ? obj.description : '',
					impactType: 'C',
					formsRepositoryId: obj.formsRepositoryId
						? obj.formsRepositoryId
						: 0,
					id: obj.id ? obj.id : 0
				});
			} else {
				result = buildImpactField({
					col: obj.col,
					dot: obj.dot,
					row: obj.row,
					formsRepositoryId: obj.formsRepositoryId
						? obj.formsRepositoryId
						: 0,
					id: obj.id ? obj.id : 0
				});
			}

			this.$emit('change', result);
		}
	},
	watch: {
		resetValue: {
			handler(val) {
				if (val) {
					Object.keys(this.$refs).forEach(k => {
						if (this.$refs[k].$v) {
							this.$refs[k].$v.$reset();
						}
					});
				}
			}
		},
		snippet: {
			deep: true,
			immediate: true,
			handler() {
				this.snippetSelected = { ...this.snippet };
			}
		},
		impactList: {
			deep: true,
			immediate: true,
			handler() {
				this.prompTypes = this.getPrompList();
			}
		},
		snippetSelected: {
			deep: true,
			immediate: true,
			handler() {
				if (this.source.impactType === 'C') {
					this.updateCodeFieldType();
				}
			}
		},
		codeSelected: {
			handler(val) {
				if (
					val &&
					Object.keys(val).length === 0 &&
					Object.getPrototypeOf(val) === Object.prototype
				) {
					this.showNewCodeModal = true;
				} else {
					if (this.showCodeListModal) {
						this.showCodeListModal = false;
						this.showNewCodeModal = true;
					} else {
						this.showCodeListModal = false;
						this.showNewCodeModal = false;
					}
				}
			}
		},
		resetSnippet: {
			handler(val) {
				if (val) {
					this.snippetSelected = {};
					this.$store.dispatch('codemanager/resetSnippet', false);
				}
			}
		},
		source: {
			deep: true,
			handler(obj) {
				const { impactType, truncate } = obj;
				if (impactType && truncate) {
					this.$nextTick(() => {
						this.$refs['characterNumber'].$refs[
							'fieldinput'
						].focus();
					});
				}
			}
		},
		setRowFocus: {
			immediate: true,
			handler(val) {
				if (val) {
					this.$nextTick(() => {
						this.$refs['row'].$refs['fieldinput'].focus();
					});
				}
			}
		}
	}
};
</script>
<style lang="scss" scoped>
.field-detail {
	@include mq($from: sm-l) {
		max-width: 340px;
	}

	.edit-form-builder & {
		@include mq($from: sm-l, $until: md) {
			max-width: 100%;
		}
	}

	&__common-fields {
		display: grid;
		gap: $gutter-lg;
		margin-bottom: $gutter-lg;
		grid-template-columns: repeat(3, 1fr);
	}

	&__dropdown-fields {
		display: grid;
		gap: $gutter-lg;
		align-items: end;
		grid-template-columns: repeat(auto-fit, minmax(101px, 1fr));
	}

	&__type-selector {
		grid-column: 1 / 3;
	}

	&__dropdown-menu {
		.menu-button,
		::v-deep .btn {
			width: 100%;
		}
	}

	&__static-fields,
	&__customer-input-fields,
	&__code-fields {
		display: grid;
		gap: $gutter-lg;
		margin-top: $gutter-lg;
	}
}

/******* Code Field Type ********/
.field-detail__code-fields {
	grid-template-columns: 1fr;
}

.field-detail__truncate {
	display: grid;
	gap: $gutter-lg;
	grid-template-columns: repeat(auto-fit, minmax(101px, 1fr));

	.fieldset {
		grid-column: 1 / 3;
	}
}
</style>
